import type { CreditAmount, StripeSubscription, UserAccountDoc } from '../firestore/schemas';
import { isCreditAmount } from './billing';

/** The quota month in force at `now`: a due `nextMonthStart` counts as an unused month of the plan's grant (the stored total when no grant is stored). */
export const effectiveMonthlyQuota = (account: UserAccountDoc | undefined, now: Date): CreditAmount | undefined => {
  const monthlyQuota = account?.monthlyQuota;
  if (!isQuotaMonthDue(account, now) || !isCreditAmount(monthlyQuota)) return monthlyQuota;
  return { total: account.stripeSubscription.monthlyGrant ?? monthlyQuota.total, used: 0 };
};

/**
 * Whether the account's stored `nextMonthStart` has been reached within the stored period, so a new quota month is in force.
 * After `currentPeriodEnd` the subscription sync owns the next month, so a lost cancellation never grants a fresh one.
 * Only an `active` subscription starts one, so a declined renewal's dunning grants no month past the one unpaid month.
 */
export const isQuotaMonthDue = (
  account: UserAccountDoc | undefined,
  now: Date
): account is UserAccountDoc & { nextMonthStart: string; stripeSubscription: StripeSubscription } =>
  nextMonthStartPassed(account, now) &&
  account.stripeSubscription?.status === 'active' &&
  Date.parse(account.stripeSubscription.currentPeriodEnd) > now.getTime();

/** Whether the account's stored `nextMonthStart` has been reached, whatever the subscription's status or period. */
export const nextMonthStartPassed = (
  account: UserAccountDoc | undefined,
  now: Date
): account is UserAccountDoc & { nextMonthStart: string } =>
  account?.nextMonthStart !== undefined && Date.parse(account.nextMonthStart) <= now.getTime();

/**
 * The first quota-month boundary of the period after `now` and before `currentPeriodEnd`, or undefined when none is.
 * A non-trialing yearly period splits into calendar months counted from `currentPeriodStart`.
 */
export const followingMonthBoundary = (subscription: StripeSubscription, now: Date): string | undefined => {
  const start = new Date(subscription.currentPeriodStart);
  const end = Date.parse(subscription.currentPeriodEnd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end)) return undefined;
  if (subscription.status === 'trialing' || subscription.interval !== 'year') return undefined;

  for (let months = 1; ; months++) {
    const boundary = addUtcMonths(start, months);
    if (boundary >= end) return undefined;
    if (boundary > now.getTime()) return new Date(boundary).toISOString();
  }
};

/** Clamps the day to the target month's last day, as Stripe does for billing anchors. */
const addUtcMonths = (start: Date, months: number) => {
  const year = start.getUTCFullYear();
  const month = start.getUTCMonth() + months;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return Date.UTC(
    year,
    month,
    Math.min(start.getUTCDate(), lastDay),
    start.getUTCHours(),
    start.getUTCMinutes(),
    start.getUTCSeconds(),
    start.getUTCMilliseconds()
  );
};
